import { Link } from "react-router-dom";
import { ArrowUpRight, Award, ClipboardCheck, ShieldCheck, Users } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const inspectorImage = "/assets/luis.jpg";

export default function About() {
  const { t } = useT();
  return (
    <div data-testid="about-page">
      <section className="bg-slate-50 py-24 md:py-32 px-5 md:px-8 border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline">{t("about.hero.overline")}</div>
            <h1 className="font-display text-5xl md:text-7xl font-black text-[#1F3460] mt-4 leading-[0.95]">
              {t("about.hero.title1")} <br />
              <span className="text-[#6FA8DC]">{t("about.hero.title2")}</span>
            </h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-[#5E6B85] text-lg">{t("about.hero.body")}</p>
          </div>
        </div>
      </section>

      <section data-testid="about-story" className="py-24 md:py-32 px-5 md:px-8">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="overline">{t("about.story.overline")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-[#1F3460] mt-4 leading-tight">
              {t("about.story.title1")} <br className="hidden md:block" />
              {t("about.story.title2")}
            </h2>
            <p className="mt-6 text-[#5E6B85] text-lg leading-relaxed">
              <span className="float-left font-display font-black text-7xl leading-[0.85] text-[#1F3460] mr-3 mt-1">B</span>
              {t("about.story.p1_after_b")}
            </p>
            <p className="mt-4 text-[#5E6B85] text-lg leading-relaxed">{t("about.story.p2")}</p>
            <p className="mt-4 text-[#5E6B85] text-lg leading-relaxed">{t("about.story.p3")}</p>
            <p className="mt-4 text-[#5E6B85] text-lg leading-relaxed">
              {t("about.story.p4_start")}<strong className="text-[#1F3460]">{t("about.story.p4_bold")}</strong>{t("about.story.p4_end")}
            </p>
            <div className="mt-8">
              <Link to="/appointment" data-testid="about-cta-appointment" className="btn-primary">
                {t("about.story.cta")} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="md:col-span-5 order-1 md:order-2">
            <img src={inspectorImage} alt="Luis Salazar, founder of Bright FL Inspections" className="w-full max-w-md mx-auto md:mx-0 md:ml-auto h-auto block" />
          </div>
        </div>
      </section>

      <section data-testid="about-values" className="bg-[#1F3460] text-white py-24 md:py-32 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="overline text-[#6FA8DC]">{t("about.values.overline")}</div>
          <h2 className="font-display text-4xl md:text-5xl font-black mt-4 max-w-3xl leading-tight">{t("about.values.title")}</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {[
              { icon: ShieldCheck, title: "about.values.v1.title", copy: "about.values.v1.copy" },
              { icon: ClipboardCheck, title: "about.values.v2.title", copy: "about.values.v2.copy" },
              { icon: Award, title: "about.values.v3.title", copy: "about.values.v3.copy" },
              { icon: Users, title: "about.values.v4.title", copy: "about.values.v4.copy" },
            ].map((v, i) => (
              <div key={i} data-testid={`value-card-${i}`} className="bg-[#1F3460] p-8 md:p-10 hover:bg-[#2A4380] transition-colors">
                <v.icon className="w-9 h-9 text-[#6FA8DC]" />
                <h3 className="font-display text-2xl font-black mt-6">{t(v.title)}</h3>
                <p className="mt-3 text-slate-300 text-sm">{t(v.copy)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="about-coverage" className="py-24 md:py-32 px-5 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <div className="overline">{t("about.coverage.overline")}</div>
            <h2 className="font-display text-4xl md:text-5xl font-black text-[#1F3460] mt-4 leading-tight">{t("about.coverage.title")}</h2>
            <p className="mt-6 text-[#5E6B85] text-lg">{t("about.coverage.body")}</p>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-px bg-[#E2E8F0]">
            {["Lee County", "Collier County", "Charlotte County", "Hendry County", "Sarasota County", "DeSoto County", "Cape Coral", "Fort Myers", "Naples", "Bonita Springs", "Estero", "Punta Gorda"].map((c) => (
              <div key={c} className="bg-white p-5 text-sm font-bold uppercase tracking-wider text-[#1F3460]">{c}</div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
